import React, { Component } from "react";
import axios from "axios";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import moment from "moment";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { observer, inject } from "mobx-react";

import API from '../../utils/api';
import {
  api2AllPlans,
  api2StatBilled, api2statCreditPlans,
  api2StatEnrolled, api2statIncomePlans,
  api2StatPlans, api2StatPlansCredit
} from '../../utils/operations';
import { moneyFormatter } from '../../utils/misc';

const green500 = '#4caf50';
const blue500 = '#2196f3';
const red500 = '#f44336';

class FinacialCharts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      orgId: props.Auth.orgId,
      fromDate: moment().subtract(1,'year').month(7).date(1).toDate(),
      toDate: moment().month(7).toDate(),
      statBilled: [],
      statEnrolled: [],
      statPlans: [],
      statPlansCredit: [],
      statPlansIncome: [],

      showProgress: true,
      chartNumber: 10,
      reBar: [],
      amountPaid: [],
      organisations: [],
      invoices: [],
      children: [],
      chart: "",
      pieColors: [
        "#223d78",
        "#04509d",
        "#2068b2",
        "#317fc2",
        "#4b9dd5",
        "#64b5e4",
        "#83c8ee",
        "#a3dcf6",
        "#c6eaf9",
      ],
      pieColorsOld: [
        "#0088FE",
        "#00C49F",
        "#FFBB28",
        "#5E35B1",
        "#6D4C41",
        "#546E7A",
        "#FF8042"
      ],
      archiveData: [],
      creditsPlans: [],
      incomePlans: [],
      programIncomePlansFilter: [],
      api2AllPlans: [],
      creditsSelectedPrograms: [],
      incomeSelectedPrograms: [],
      selectedPrograms: [],
      firstMonth: "September",
      firstYear: 2017,
      secondMonth: "September",
      secondYear: 2019,
      filterArchive: false
    };
  }

  planColors = [
    "#223d78", "#04509d", "#2068b2", "#317fc2",
    "#4b9dd5", "#64b5e4", "#83c8ee", "#a3dcf6", "#c6eaf9",
    '#FF6633', '#FFB399', '#FF33FF', '#00B3E6',
    '#E6B333', '#3366E6', '#999966', '#99FF99', '#B34D4D',
    '#80B300', '#809900', '#E6B3B3', '#6680B3', '#66991A',
    '#FF99E6', '#CCFF1A', '#FF1A66', '#E6331A', '#33FFCC',
    '#66994D', '#B366CC', '#4D8000', '#B33300', '#CC80CC',
    '#66664D', '#991AFF', '#E666FF', '#4DB3FF', '#1AB399',
    '#E666B3', '#33991A', '#CC9999', '#B3B31A', '#00E680',
    '#4D8066', '#809980', '#E6FF80', '#1AFF33', '#999933',
    '#FF3380', '#CCCC00', '#66E64D', '#4D80CC', '#9900B3',
    '#E64D66', '#4DB380', '#FF4D4D', '#99E6E6', '#6666FF'];

  componentDidMount() {
    if (!this.props.Auth.can('dashboard')) return this.props.history.push("/children");
    this.loadData()
  }

  loadData = () => {
    let that = this;

    Promise.all([
      this.loadBilledData(),
      this.loadEnrolledData(),
      this.loadPlanData(),
      this.loadPlanCreditData(),
      this.loadPlanIncomeData(),
    ]).then()

    axios
      .all([
        API.get(api2statCreditPlans),
        API.get(api2statIncomePlans),
        API.get(api2AllPlans, { params: {in_graphs: true}}),
      ]).then(
        axios.spread(function(
          creditsPlans,
          incomePlans,
          api2AllPlans,
        ) {
          that.setState({
            creditsPlans: creditsPlans.data,
            incomePlans: incomePlans.data,
            api2AllPlans: api2AllPlans.data,
            showProgress: false,
          });
        })
      );
  };

  loadPlanCreditData = async () => {
    const response = await API.post(api2StatPlansCredit, {
      planIds: this.state.creditsSelectedPrograms,
      fromDate: this.state.fromDate,
      toDate: this.state.toDate,
      orgId: this.state.orgId,
    })
    this.setState({statPlansCredit: response.data})
  }

  loadPlanIncomeData = async () => {
    const response = await API.post(api2StatPlansCredit, {
      planIds: this.state.incomeSelectedPrograms,
      fromDate: this.state.fromDate,
      toDate: this.state.toDate,
      orgId: this.state.orgId,
    })
    this.setState({statPlansIncome: response.data})
  }

  loadPlanData = async () => {
    const response = await API.post(api2StatPlans, {
      planIds: this.state.selectedPrograms,
      fromDate: this.state.fromDate,
      toDate: this.state.toDate,
      orgId: this.state.orgId,
    })
    this.setState({statPlans: response.data})
  }

  loadBilledData = async () => {
    const response = await API.post(api2StatBilled, {
        fromDate: this.state.fromDate,
        toDate: this.state.toDate,
        orgId: this.state.orgId,
    })
    this.setState({statBilled: response.data})
  }

  loadEnrolledData = async () => {
    const response = await API.post(api2StatEnrolled, {
      fromDate: this.state.fromDate,
      toDate: this.state.toDate,
      orgId: this.state.orgId,
    })
    this.setState({statEnrolled: response.data})
  }

  showBilledAmount = () => {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={this.state.statBilled}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis dataKey="s" />
          <YAxis dataKey={(v) => parseInt(v.billed)}  />
          <CartesianGrid strokeDasharray="4 4" />
          <Tooltip formatter={(value) => { return moneyFormatter(value) }} />
          <Legend />
          <Bar name="Billed" dataKey="billed" fill={blue500} />
          <Bar name="Collected" dataKey="paid" fill={green500} />
        </BarChart>
      </ResponsiveContainer>
    );
  };


  showBilledPercent = () => {
    return (
      <BarChart
        width={800}
        height={400}
        data={this.state.statBilled}
        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
      >
        <XAxis dataKey="s" />
        <YAxis  />
        <CartesianGrid strokeDasharray="4 4" />
        <Tooltip />
        <Legend />
        <Bar unit="%" name="Collected" dataKey={(v) => Math.round(v.paid / v.billed * 100)} fill={blue500} />
        <Bar unit="%" name="Outstanding" dataKey={(v) => Math.round(v.paid / v.billed * 100 - 100)} fill={red500} />
      </BarChart>
    );
  };

  showChildrenEnrollment = () => {
    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={this.state.statEnrolled}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <XAxis dataKey="s" />
          <YAxis />
          <CartesianGrid strokeDasharray="4 4" />
          <Tooltip />
          <Legend />
          <Bar name="Children quantity" maxBarSize={40} dataKey="qty" fill={blue500} />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Collects every programId seen across all periods (not just the first one) together
  // with a name for each -- a period's `data` map only contains entries for programs with
  // activity that period, so periods can disagree on which programIds are present.
  planIdsWithNames = (periods) => {
    const names = {};
    periods.forEach(period => {
      Object.entries(period.data).forEach(([programId, info]) => {
        if (info && info.name) names[programId] = info.name;
      });
    });
    return Object.entries(names);
  }

  showChildrenPrograms = () => {
    const barTooltip = (value, name, props) => {
      const item = Object.values(props.payload?.data || {}).find(row => row.name === name)
      return item ? `${item.qty} kids, ` + moneyFormatter(value*item.price) : `${value} kids`;
    }


    return (
      <div>
        {this.state.statPlans.length > 0 && <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={this.state.statPlans}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis dataKey="period" />
            <YAxis />
            <CartesianGrid strokeDasharray="4 4" />
            <Tooltip formatter={barTooltip} />
            <Legend />
            {this.planIdsWithNames(this.state.statPlans).map(([programId, name], index) => {
              return <Bar key={programId} name={name} maxBarSize={40} dataKey={(v) => v.data[programId]?.qty ?? 0} fill={this.planColors[index]} />
            })}
          </BarChart>
        </ResponsiveContainer>}
      </div>
    );
  }

  showChildrenProgramsCredits = () => {
    const barTooltip = (value, name, props) => {
      const item = Object.values(props.payload?.data || {}).find(row => row.name === name)
      return item ? `${item.children_qty} kids, ` + moneyFormatter(value) : moneyFormatter(value);
    }

    return (
      <div>
        {this.state.statPlansCredit.length > 0 && <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={this.state.statPlansCredit}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis dataKey="period" />
            <YAxis />
            <CartesianGrid strokeDasharray="4 4" />
            <Tooltip formatter={barTooltip} />
            <Legend />
            {this.planIdsWithNames(this.state.statPlansCredit).map(([programId, name], index) => {
              return <Bar key={programId} name={name} maxBarSize={40} dataKey={(v) => v.data[programId]?.credits ?? 0} fill={this.planColors[index]} />
            })}
          </BarChart>
        </ResponsiveContainer>}
      </div>
    );
  }

  showChildrenProgramsIncome = () => {
    const barTooltip = (value, name, props) => {
      const item = Object.values(props.payload?.data || {}).find(row => row.name === name)
      return item ? `${item.children_qty} kids, ` + moneyFormatter(value) : moneyFormatter(value);
    }

    return (
      <div>
        {this.state.statPlansIncome.length > 0 && <ResponsiveContainer width="100%" height={400}>
          <BarChart
            data={this.state.statPlansIncome}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <XAxis dataKey="period" />
            <YAxis />
            <CartesianGrid strokeDasharray="4 4" />
            <Tooltip formatter={barTooltip} />
            <Legend />
            {this.planIdsWithNames(this.state.statPlansIncome).map(([programId, name], index) => {
              return <Bar key={programId} name={name} maxBarSize={40} dataKey={(v) => v.data[programId]?.credits ?? 0} fill={this.planColors[index]} />
            })}
          </BarChart>
        </ResponsiveContainer>}
      </div>
    );
  }

  handleChangeFromDate = (date) => {
    this.setState({fromDate: date});
    setTimeout(() => this.loadData(), 500)
  }

  handleChangeToDate = (date) => {
    this.setState({toDate: date});
    setTimeout(() => this.loadData(), 500)
  }

  handleChange = (event) => {
    this.setState({ chartNumber: event.target.value, filterArchive: false });
  };

  handleChangeCreditsPlans = (event) => {
    this.setState({creditsSelectedPrograms: event.target.value})
    setTimeout(() => this.loadPlanCreditData(), 500)
  };

  handleChangeIncomePlans = (event) => {
    this.setState({incomeSelectedPrograms: event.target.value})
    setTimeout(() => this.loadPlanIncomeData(), 500)
  };

  handleChangePlans = (event) => {
    this.setState({selectedPrograms: event.target.value})
    setTimeout(() => this.loadPlanData(), 500)
  };

  render() {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <div style={{ margin: "auto" }}>
          <br />
          <FormControl sx={{ minWidth: 250, mb: 2 }}>
            <InputLabel>Chart</InputLabel>
            <Select label="Chart" value={this.state.chartNumber} onChange={this.handleChange}>
              <MenuItem value={10}>Billed/Paid Amount</MenuItem>
              <MenuItem value={12}>Children Enrollment</MenuItem>
              <MenuItem value={13}>Programs by Number Of Children</MenuItem>
              <MenuItem value={14}>Programs Credits</MenuItem>
              <MenuItem value={15}>Programs Income</MenuItem>
            </Select>
          </FormControl>
          <div className="dashboardFilters">
            <DatePicker
              label="From Date"
              value={this.state.fromDate}
              onChange={this.handleChangeFromDate}
              slotProps={{ textField: { style: { marginLeft: 20 } } }}
            />
            <DatePicker
              label="To Date"
              value={this.state.toDate}
              onChange={this.handleChangeToDate}
              slotProps={{ textField: { style: { marginLeft: 20 } } }}
            />
            {(this.state.chartNumber === 13) &&
              <FormControl style={{ marginLeft: 40, minWidth: 200 }}>
                <InputLabel>Programs</InputLabel>
                <Select
                  label="Programs"
                  multiple
                  value={this.state.selectedPrograms}
                  onChange={this.handleChangePlans}
                >
                  {this.state.api2AllPlans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
            {(this.state.chartNumber === 14) &&
              <FormControl style={{ marginLeft: 40, minWidth: 200 }}>
                <InputLabel>Credit Programs</InputLabel>
                <Select
                  label="Credit Programs"
                  multiple
                  value={this.state.creditsSelectedPrograms}
                  onChange={this.handleChangeCreditsPlans}
                >
                  {this.state.creditsPlans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
            {(this.state.chartNumber === 15) &&
              <FormControl style={{ marginLeft: 40, minWidth: 200 }}>
                <InputLabel>Income Programs</InputLabel>
                <Select
                  label="Income Programs"
                  multiple
                  value={this.state.incomeSelectedPrograms}
                  onChange={this.handleChangeIncomePlans}
                >
                  {this.state.incomePlans.map((plan) => (
                    <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            }
          </div>
          <br />
          <br />
          <br />
          <div style={{ overflowX: "scroll"}}>
            {this.state.chartNumber === 10 && this.showBilledAmount()}
            {/*{this.state.chartNumber === 11 && this.showBilledPercent()}*/}
            {this.state.chartNumber === 12 && this.showChildrenEnrollment()}
            {this.state.chartNumber === 13 && this.showChildrenPrograms()}
            {this.state.chartNumber === 14 && this.showChildrenProgramsCredits()}
            {this.state.chartNumber === 15 && this.showChildrenProgramsIncome()}
          </div>
          <br />
        </div>
      </LocalizationProvider>
    );
  }
}

export default inject("Auth")(observer(FinacialCharts))
